import { EncryptedKey } from '../models/encrypted-key';
// Web Crypto API - SubtleCrypto Guideline:
// https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto
//
// This feature is available only in secure contexts (HTTPS) !!!
export class CryptoService {
    constructor() {
        this.numOfIterations = 100000;
    }
    /**
     * Generates a private and public key pair
     *
     * @returns Returns a Promise that fulfills with a CryptoKeyPair
     *
     */
    async generateKey() {
        return await crypto.subtle.generateKey({
            name: Algorithm.ECDSA,
            namedCurve: NamedCurve.P384,
        }, true, [Usage.Sign, Usage.Verify]);
    }
    async exportPrivateKey(privateKey) {
        return await crypto.subtle.exportKey(Format.PKCS8, privateKey);
    }
    async exportPublicKey(publicKey) {
        return await crypto.subtle.exportKey(Format.SubjectPublicKeyInfo, publicKey);
    }
    async importPrivateKey(privateKeyDer) {
        return await crypto.subtle.importKey(Format.PKCS8, privateKeyDer, {
            name: Algorithm.ECDSA,
            namedCurve: NamedCurve.P384,
        }, true, [Usage.Sign]);
    }
    async importPublicKey(publicKeyDer) {
        return await crypto.subtle.importKey(Format.SubjectPublicKeyInfo, publicKeyDer, {
            name: Algorithm.ECDSA,
            namedCurve: NamedCurve.P384,
        }, true, [Usage.Verify]);
    }
    /**
     * Generates a digital signature
     *
     * @returns Returns a Promise that fulfills with an ArrayBuffer containing the signature
     *
     */
    async sign(privateKey, data) {
        return await crypto.subtle.sign({
            name: Algorithm.ECDSA,
            hash: { name: Algorithm.SHA_512 },
        }, privateKey, data);
    }
    /**
     * Verifies a digital signature
     *
     * @returns Returns a Promise<boolean>.
     *
     */
    async verifySignature(publicKey, data, signature) {
        const verificationResult = await crypto.subtle.verify({
            name: Algorithm.ECDSA,
            hash: { name: Algorithm.SHA_512 },
        }, publicKey, signature, data);
        return verificationResult;
    }
    // export and encrypt private key
    async encryptPrivateKey(signatureKey, salt, privateKey) {
        // generate a new initialization vector
        const iv = crypto.getRandomValues(new Uint8Array(12));
        // import password
        const keyMaterial = await this.getKeyMaterial(signatureKey);
        // derive a secret key from password
        const wrappingKey = await this.getWrappingKey(keyMaterial, salt);
        // export and encrypt private key
        const encryptedKey = await this.wrapKey(privateKey, wrappingKey, iv);
        return new EncryptedKey(encryptedKey, iv);
    }
    // decrypt and import private key
    async decryptPrivateKey(signatureKey, salt, encryptedPrivateKey, iv) {
        // import password
        const keyMaterial = await this.getKeyMaterial(signatureKey);
        // derive a secret key from password
        const wrappingKey = await this.getWrappingKey(keyMaterial, salt);
        // decrypt and import private key
        const privateKey = await this.unwrapKey(encryptedPrivateKey, wrappingKey, iv);
        return privateKey;
    }
    async sha256(payload) {
        return await crypto.subtle.digest("SHA-256", payload);
    }
    // import password: it takes as input a key in an external, portable format and gives you a CryptoKey
    async getKeyMaterial(signatureKey) {
        return await crypto.subtle.importKey(Format.Raw, signatureKey, { name: Algorithm.PBKDF2 }, false, [Usage.DeriveBits, Usage.DeriveKey]);
    }
    // Get Wrap / Unwrap Key: derive a secret key from a master key
    async getWrappingKey(keyMaterial, salt) {
        return await crypto.subtle.deriveKey({
            name: Algorithm.PBKDF2,
            salt,
            iterations: this.numOfIterations,
            hash: Algorithm.SHA_256,
        }, keyMaterial, { name: Algorithm.AES_GCM, length: 256 }, true, [Usage.WrapKey, Usage.UnwrapKey]);
    }
    // exports the key in an external, portable format, then encrypts the exported key
    async wrapKey(privateKey, wrappingKey, iv) {
        return await crypto.subtle.wrapKey(Format.PKCS8, privateKey, wrappingKey, {
            name: Algorithm.AES_GCM,
            iv,
        });
    }
    // decrypts the key and then imports it
    async unwrapKey(encryptedPrivateKey, wrappingKey, iv) {
        return await crypto.subtle.unwrapKey(Format.PKCS8, encryptedPrivateKey, wrappingKey, {
            name: Algorithm.AES_GCM,
            iv
        }, {
            name: Algorithm.ECDSA,
            namedCurve: NamedCurve.P384,
        }, true, [Usage.Sign]);
    }
}
var Format;
(function (Format) {
    Format["Raw"] = "raw";
    Format["PKCS8"] = "pkcs8";
    Format["SubjectPublicKeyInfo"] = "spki";
})(Format || (Format = {}));
var Algorithm;
(function (Algorithm) {
    Algorithm["SHA_256"] = "SHA-256";
    Algorithm["SHA_512"] = "SHA-512";
    Algorithm["AES_GCM"] = "AES-GCM";
    Algorithm["ECDSA"] = "ECDSA";
    Algorithm["PBKDF2"] = "PBKDF2";
})(Algorithm || (Algorithm = {}));
var NamedCurve;
(function (NamedCurve) {
    NamedCurve["P256"] = "P-256";
    NamedCurve["P384"] = "P-384";
    NamedCurve["P521"] = "P-521";
})(NamedCurve || (NamedCurve = {}));
var Usage;
(function (Usage) {
    Usage["Sign"] = "sign";
    Usage["Verify"] = "verify";
    Usage["DeriveBits"] = "deriveBits";
    Usage["DeriveKey"] = "deriveKey";
    Usage["WrapKey"] = "wrapKey";
    Usage["UnwrapKey"] = "unwrapKey";
})(Usage || (Usage = {}));
